import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import TextInput from "../../../../../Components/TextInput";
import Textarea from "../../../../../Components/TextArea";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";

import { checkText } from "../../../../../Utils/Validator";
import { paymentTrackerCreateAPI, paymentTrackerDeleteAPI, paymentTrackerGetDataApi, paymentTrackerUpdateAPI } from "../../../../../Api/Project/DefaultServices/PaymentTrackerApi";
import { PaymentTrackerService } from "../../../../../Controllers/Types/Project/DefaultService/PaymentTrackerInterface";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import TableActionLoader from "../../../../../Components/TableActionLoader";

const PaymentTracker: React.FC = () => {
    const location = useLocation();
    const projectData = location.state?.data;
    const serviceId = Number(projectData.folder_id);

    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // const isShared: boolean = projectData.sharedBy !== null ? true : false;
 const isShared: boolean = projectData.can_edit===false ? true : false;

    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loading, setLoading] = useState<boolean>(false);
    const [paymentTrackerData, setPaymentTrackerData] = useState<PaymentTrackerService[]>([]);
    const [selectedPayTrackData, setSelectedPayTrackData] = useState<PaymentTrackerService | null>(null);
    const [showServiceModal, setShowServiceModal] = useState<boolean>(false);
    const [serviceName, setServiceName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [updateHasChanges, setUpdateHasChanges] = useState<boolean>(false);
    const [deleteLoadingId, setdeleteLoadingId] = useState<number | null>(null);

    const [serviceNameError, setServiceNameError] = useState<string>('');
    const [descriptionError, setDescriptionError] = useState<string>('');

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermission_add_payment_tracker = useCanEdit('Payment Tracker', 'add_payment_tracker');
    const isPermission_edit_payment_tracker = useCanEdit('Payment Tracker', 'edit_payment_tracker');
    const isPermission_delete_payment_tracker = useCanEdit('Payment Tracker', 'delete_payment_tracker');


    /* ******************************** For service ********************************** */
    const handleAddService = () => {
        setDataState('Add');
        setShowServiceModal(true);
    }

    const handleClearFields = () => {
        setServiceName('');
        setDescription('');

        setSelectedPayTrackData(null);
    }

    const handleClearErrors = () => {
        setServiceNameError('');
        setDescriptionError('');
    }

    useEffect(() => {
        paymentTrackerDataGet();
    }, [serviceId]);


    /* ********************************* get data list ************************************************** */
    const paymentTrackerDataGet = async () => {
        if (serviceId) {
            setDataLoading(true);
            const data = await paymentTrackerGetDataApi(Number(serviceId));
            setDataLoading(false);
            setInitialDataLoading(false);

            if (data.status) {
                setPaymentTrackerData(data.data || []);
            } else {
                setErrorMessage(`An error occurred while retrieving ${(projectData.feature_name).toLowerCase()} data.`);
            }
        }
    }
    /* ********************************* get data list ************************************************** */

    useEffect(() => {
        checkText(serviceName, false, setServiceNameError);
    }, [serviceName]);

    useEffect(() => {
        checkText(description, false, setDescriptionError)
    }, [description]);

    function checkCredentials(): boolean {
        const isServiceNameValid = checkText(serviceName, true, setServiceNameError);
        const isDescriptionValid = checkText(description, false, setDescriptionError);

        return isServiceNameValid && isDescriptionValid;
    }

    /* ********************************** Create ************************************************************ */
    const handleCreateService = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);

            const data = await paymentTrackerCreateAPI(
                Number(serviceId),
                serviceName,
                description
            );

            setLoading(false);

            if (data.statusCode === 200) {
                paymentTrackerDataGet();
                handleCloseServiceModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Service created successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating service.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ********************************** Create ************************************************************ */


    /* ********************************** Update ************************************************************ */
    const handleUpdateService = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);

            const data = await paymentTrackerUpdateAPI(
                Number(selectedPayTrackData?.id!),
                serviceName,
                description
            );

            setLoading(false);

            if (data.statusCode === 200) {
                paymentTrackerDataGet();
                handleCloseServiceModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Service updated successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating the service.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ********************************** Update ************************************************************ */


    /* ********************************** Delete ************************************************************ */
    const handleDeleteData = (payTackId: number): void => {
        confirmDialog({
            message: (
                <div className="text-center d-flex justify-content-center flex-column">
                    <span>
                        Are you sure you want to delete the <b>{selectedPayTrackData?.service_name}</b> service detail?<br />
                        <small className="text-center text-secondary first_letter_capitalized">All the documents inside the service will also be deleted.</small>
                    </span>
                </div>
            ),
            header: 'Confirm the deletion',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteData(payTackId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const deleteData = async (payTackId: number) => {
        setdeleteLoadingId(payTackId);
        const data = await paymentTrackerDeleteAPI(payTackId);

        setdeleteLoadingId(null);
        if (data.statusCode === 200) {
            paymentTrackerDataGet();
            handleCloseServiceModal();

            if (toast.current) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Service data deleted successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error deleting service data.',
                    life: 3000
                });
            }
        }
    }
    /* ********************************** Delete ************************************************************ */

    useEffect(() => {
        if (selectedPayTrackData) {
            if (serviceName !== selectedPayTrackData?.service_name) {
                setUpdateHasChanges(true);
            } else if (description !== selectedPayTrackData?.description) {
                setUpdateHasChanges(true);
            } else {
                setUpdateHasChanges(false);
            }
        }
    }, [selectedPayTrackData, serviceName, description]);

    const handleEditService = (data: PaymentTrackerService) => {
        setDataState('Edit');
        setShowServiceModal(true);
        setSelectedPayTrackData(data);

        setServiceName(data.service_name);
        setDescription(data.description);
    }

    const handleCloseServiceModal = () => {
        setShowServiceModal(false);

        handleClearFields();
        handleClearErrors();
    }

    /* Modal header */
    const serviceModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;{(projectData.feature_name).toLowerCase()} service
            </h5>

            {dataState === 'Edit' ? (
                <React.Fragment>
                    {deleteLoadingId === selectedPayTrackData?.id ? (
                        <TableActionLoader className="is_modal_loader" />
                    ) : (
                        <React.Fragment>
                            {isPermission_delete_payment_tracker && (
                                <button type="button" className="close_modal_btn p-ripple"
                                    onClick={() => handleDeleteData(selectedPayTrackData?.id!)}>
                                    <i className="bi bi-trash3"></i>
                                    <Ripple />
                                </button>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <button type="button" className="close_modal_btn p-ripple"
                    onClick={handleCloseServiceModal}>
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    );

    /* Modal footer */
    const serviceModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseServiceModal}
            />

            <Button
                label={`${loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}`}
                onClick={dataState === 'Add' ? handleCreateService : handleUpdateService}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !updateHasChanges}
            />
        </div>
    );
    /* *************************************************************************** */


    /* ***************************** for table view ****************************** */
    const tableActionBody = (rowData: PaymentTrackerService) => {
        return (
            <div className="table_action_btn_area">
                <button className="table_action_btn p-ripple"
                    onClick={() => {
                        navigate(`/project/detail/default-service/onsite/payment-tracker/service`, {
                            state: { projectData: projectData, paymentTrackerId: rowData.id, paymentTrackerService: rowData.service_name }
                        });
                    }}
                >
                    View
                    <Ripple />
                </button>

                {isPermission_edit_payment_tracker && !isShared && (
                    <button className="table_action_btn p-ripple"
                        onClick={() => handleEditService(rowData)}
                    >
                        Edit
                        <Ripple />
                    </button>
                )}
            </div>
        )
    }
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */
    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading && initialDataloading ? (
                    <PageLoader />
                ) : errorMessage ? (
                    <ErrorPage errorMessage={errorMessage} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area justify-content-end mt-sm-3 mt-3">
                            {isPermission_add_payment_tracker && !isShared && (
                                <div className="page_action_area">
                                    <button className="add_data_link_btn p-ripple" onClick={handleAddService}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add certificate</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section">
                            {paymentTrackerData?.length > 0 ? (
                                <DataTable
                                    value={paymentTrackerData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    loading={dataLoading}
                                    scrollable
                                >
                                    <Column
                                        header="Title"
                                        headerClassName="text_purple"
                                        field="service_name"
                                        body={(rowData: PaymentTrackerService) => (
                                            <span className="text_bold text-capitalize">
                                                {rowData.service_name ? rowData.service_name : '---'}
                                            </span>
                                        )}
                                        style={{ width: '25%' }}
                                    ></Column>

                                    <Column
                                        header="File"
                                        headerClassName="text_purple"
                                        field="payment_tracker_document_count"
                                        body={(rowData: PaymentTrackerService) => (
                                            <span className="text_bold">
                                                {rowData.payment_tracker_document_count}&nbsp;file{rowData.payment_tracker_document_count > 1 ? 's' : ''}
                                            </span>
                                        )}
                                        style={{ width: '20%' }}
                                    ></Column>

                                    <Column
                                        header="Description"
                                        headerClassName="text_purple"
                                        field="description"
                                        className="first_letter_capitalized"
                                        body={(rowData: PaymentTrackerService) => (
                                            <span>
                                                {rowData.description ? rowData.description : '---'}
                                            </span>
                                        )}
                                        style={{ width: '35%' }}
                                    ></Column>

                                    <Column
                                        body={tableActionBody}
                                        className="table_btn_area"
                                        style={{ width: '20%' }}
                                        frozen={window.innerWidth > 575}
                                        alignFrozen="right"
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No ${(projectData.feature_name).toLowerCase()} services added. ${!isShared && isPermission_add_payment_tracker ? 'To add the service click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div >


            {/* Service modal */}
            <Dialog
                header={serviceModalHeader}
                footer={serviceModalFooter}
                headerClassName="custom_modal_header"
                visible={showServiceModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseServiceModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        <div className="row">
                            {/* Title */}
                            <div className="col-12">
                                <TextInput
                                    id="serviceName"
                                    label="Title"
                                    labelHtmlFor="serviceName"
                                    required={true}
                                    value={serviceName}
                                    placeholder="Title"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setServiceName(e.target.value)}
                                    error={serviceNameError}
                                />
                            </div>

                            {/* Description */}
                            <div className="col-12">
                                <Textarea
                                    id="description"
                                    label="Description"
                                    labelHtmlFor="description"
                                    required={false}
                                    value={description}
                                    placeholder="Description"
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                                    error={descriptionError}
                                    formGroupClassName="mb-2"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </React.Fragment>
    )
}

export default PaymentTracker;